import React, { useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ArticleDTO } from "../../../../models/ArticleDTO";
import { owedArticleStatus } from "../../../../constants";
import KitHistoryTimeline from "../../../../components/kits/kitHistoryTimeline";
import KitPaymentPrompt from "../../../../components/kits/kitPaymentPrompt";
import AccionesAlumno from "../../../../components/enrollment/accionesAlumno";
import { apiError, bestEnrollment, cancelReasonLabel } from "../kits/shared";
import { Chip, Dato, EntregaChip, PagoChip, QueVaAPasar, Sub, Ventana, ampm, monto, pagoDe, vendidoPor } from "./piezasTienda";

/*
	Ventas › clic en una venta (Módulos Claros, 2026-11-01): montos, factura, entrega, Vendido Por, el historial y los botones de
	siempre (mismos endpoints y permiso de Control de Ventas, 38 Crear): Cobrar Resto / Reportar Pago (comprobante a Caja),
	Anular (solo sin ningún pago) y Reactivar, los dos con «Qué Va a Pasar».
*/

export interface DetalleVentaProps {
	campusId: number;
	sym: string;
	kit: ArticleDTO;
	/** Sección por la que se lee el historial: kitSales (38) o newSale (quien solo tiene Nueva Venta). */
	section: "kitSales" | "newSale";
	/** Bit Crear de Control de Ventas (38): Anular, Reactivar, Cobrar Resto. */
	canAct: boolean;
	onClose: () => void;
	onChanged: (aviso: string) => void;
}

const DetalleVenta: React.FC<DetalleVentaProps> = ({ campusId, sym, kit, section, canAct, onClose, onChanged }) => {
	const http = HttpManager.getInstance().kitsService;
	const [accion, setAccion] = useState<"anular" | "reactivar" | null>(null);
	const [motivo, setMotivo] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [pagando, setPagando] = useState<boolean>(false);

	const pago = pagoDe(kit);
	const enrollment = bestEnrollment(kit);
	const total = kit.subTotal?.value ?? 0;
	const pagado = kit.paidAmount?.value ?? 0;
	const falta = kit.pendingAmount?.value ?? Math.max(0, total - pagado);
	const ticketEnCaja = !!kit.paymentIntentId && kit.paymentIntentStatus === "Por procesar";
	const sinPago = !kit.isCancelled && kit.statusId === owedArticleStatus.PENDING && pagado === 0;
	const conAbono = !kit.isCancelled && kit.statusId === owedArticleStatus.PARTLY_PAID;
	const quien = kit.isThirdParty ? (kit.buyerName || "Sin Dato") : (kit.studentName || "");

	const lineas = accion === "anular" ? [
		{ texto: (kit.name || kit.title) + " de " + quien + " pasa a Anulado." },
		{ texto: "Total " + monto(sym, total) + ". No tiene pagos: no hay dinero que mover." },
		...(kit.invoiceId ? [{ texto: "La Factura #" + kit.invoiceId + " se anula si no le queda otro producto.", oro: true }] : []),
		{ texto: "Queda en el historial con tu nombre y el motivo." }
	] : [
		{ texto: (kit.name || kit.title) + " de " + quien + " vuelve a Sin Pago por " + monto(sym, total) + "." },
		{ texto: "Se cobra con una factura nueva" + (kit.invoiceId ? "; la Factura #" + kit.invoiceId + " queda como historial." : ".") },
		{ texto: "Si no se paga hoy, se anula sola al cierre del día.", oro: true },
		{ texto: "Queda en el historial con tu nombre y el motivo." }
	];

	const confirmar = async () => {
		setBusy(true);
		setError(null);
		try {
			if (accion === "anular") await http.cancel(campusId, kit.id, motivo.trim());
			else await http.reactivate(campusId, kit.id, motivo.trim());
			setAccion(null);
			onChanged(accion === "anular" ? "Venta anulada." : "Venta reactivada.");
		} catch (requestError) {
			setError(apiError(requestError, "No se pudo completar."));
		} finally {
			setBusy(false);
		}
	};

	return <>
		<Ventana onClose={onClose} busy={busy} titulo={<>
			<span>{kit.name || kit.title}{quien ? " · " + quien : ""}</span>
			<PagoChip kit={kit} />
		</>} pie={<>
			{!kit.isThirdParty && kit.studentId ? <AccionesAlumno studentId={kit.studentId} campusId={campusId} alinear="inicio" /> : null}
			<span style={{ flex: 1 }} />
			{canAct && conAbono && !ticketEnCaja ? <button type="button" className="btn btn-primary btn-sm" onClick={() => setPagando(true)}>Cobrar Resto</button> : null}
			{canAct && sinPago && !ticketEnCaja ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setPagando(true)}>Reportar Pago</button> : null}
			{canAct && sinPago && !ticketEnCaja ? <button type="button" className="btn btn-outline-secondary btn-sm waOroTexto" onClick={() => { setMotivo(""); setError(null); setAccion("anular"); }}>Anular</button> : null}
			{canAct && kit.isCancelled ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setMotivo(""); setError(null); setAccion("reactivar"); }}>Reactivar</button> : null}
		</>}>
			{kit.isCancelled ? <div className="wvtAvisoOro"><b>Anulado{kit.cancelledAt ? " " + kit.cancelledAt : ""}</b><div>{cancelReasonLabel(kit.cancelReason)}</div></div> : null}
			{ticketEnCaja ? <div className="wvtAvisoOro"><b>Pago Reportado</b><div>Ticket #{kit.paymentIntentId} en Pagos por Procesar</div></div> : null}
			<div className="wvtDatos">
				<Dato t="Total">{monto(sym, total)}{kit.quantity > 1 ? <Sub>{kit.quantity} piezas</Sub> : null}</Dato>
				<Dato t={pago.texto === "Pagado" ? "Pagado" : "Abonado"}>{monto(sym, pagado)}{kit.paymentsCount && kit.paymentsCount > 1 ? <Sub>{kit.paymentsCount} pagos</Sub> : null}</Dato>
				{!kit.isCancelled && falta > 0 ? <Dato t="Falta"><span className="wvtOro">{monto(sym, falta)}</span></Dato> : null}
				<Dato t="Factura">{kit.invoiceId ? "#" + kit.invoiceId : "Sin Factura"}</Dato>
				<Dato t="Entrega"><EntregaChip kit={kit} />{kit.deliveredAt ? <Sub>{kit.deliveredAt}</Sub> : null}</Dato>
				<Dato t="Vendido Por">{vendidoPor(kit)}</Dato>
				<Dato t={kit.firstPaidAt ? "Primer Pago" : "Cargado"}>{kit.firstPaidAt || kit.createdAt}{kit.paidAt && kit.paidAt !== kit.firstPaidAt ? <Sub>Último Pago {kit.paidAt}</Sub> : null}</Dato>
				<Dato t={kit.isThirdParty ? "Comprador" : "Curso"}>{kit.isThirdParty ? <>{kit.buyerName || "Sin Dato"}<Sub>{kit.buyerPhone || ""}</Sub></>
					: enrollment ? <>{enrollment.courseBrief || enrollment.module || enrollment.level}<Sub>{[ampm(enrollment.schedule), enrollment.mentor].filter(Boolean).join(", ")}</Sub></> : <Chip tono="tenue">Sin Horario Activo</Chip>}</Dato>
			</div>
			<div className="wvtSeccion">
				<h6>Historial</h6>
				<KitHistoryTimeline campusId={campusId} kitId={kit.id} section={section} compact />
			</div>
		</Ventana>
		{accion ? <QueVaAPasar titulo={accion === "anular" ? "Anular Venta" : "Reactivar Venta"} lineas={lineas} motivo={motivo} onMotivo={setMotivo}
			confirmar={accion === "anular" ? "Anular" : "Reactivar"} busy={busy} error={error} onCancel={() => setAccion(null)} onConfirm={confirmar} /> : null}
		{pagando ? <KitPaymentPrompt campusId={campusId} section="kitSales" kitIds={[kit.id]} studentName={quien} pendingAmount={falta || null}
			onDone={(_id, message) => { setPagando(false); onChanged(message); }} onCancel={() => setPagando(false)} /> : null}
	</>;
};

export default DetalleVenta;
